import { SORT_KEYS, SORT_LABELS, type SortState } from './customers';

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

/** Export and print buttons for a customer list. Both act on the rows currently listed. */
export function ListActions({
  count,
  onExport,
  onPrint,
}: {
  count: number;
  onExport: () => void;
  onPrint: () => void;
}) {
  const empty = count === 0;
  return (
    <div className="list-actions" role="group" aria-label="Export and print">
      <button
        type="button"
        onClick={onExport}
        disabled={empty}
        title={
          empty
            ? 'Nothing to export'
            : `Download the ${plural(count, 'listed customer')} as an Excel workbook`
        }
      >
        Export to Excel{empty ? '' : ` (${count})`}
      </button>
      <button
        type="button"
        onClick={onPrint}
        disabled={empty}
        title={empty ? 'Nothing to print' : `Print the ${plural(count, 'listed customer')}`}
      >
        Print
      </button>
    </div>
  );
}

/**
 * Heading that appears only on paper: who printed what, with the search and
 * sort that were applied, so a printed list can be understood on its own.
 */
export function PrintHeading({
  title,
  subtitle,
  account,
  search,
  sort,
  count,
}: {
  title: string;
  subtitle?: string;
  account: string;
  search: string;
  sort: SortState | null;
  count: number;
}) {
  const printed = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const sortText = sort
    ? `${SORT_LABELS[sort.key]}, ${sort.direction === 'ascending' ? 'A to Z' : 'Z to A'}`
    : 'route order';
  return (
    <div className="print-heading">
      <p className="print-brand">XTREME Technologies · Distributor Registry</p>
      <h2>{title}</h2>
      {subtitle && <p className="print-subtitle">{subtitle}</p>}
      <p className="print-meta">
        {plural(count, 'customer')} · Account: {account} · Search: {search ? `“${search}”` : 'none'}{' '}
        · Sorted by {sortText} · Printed {printed}
      </p>
    </div>
  );
}

/**
 * A compact sort control for narrow screens, where the route table becomes
 * stacked cards and the column headers are hidden.
 */
export function SortSelect({
  id,
  sort,
  onChange,
}: {
  id: string;
  sort: SortState | null;
  onChange: (sort: SortState | null) => void;
}) {
  const value = sort ? `${sort.key}:${sort.direction}` : '';
  return (
    <div className="toolbar-field sort-select">
      <label htmlFor={id}>Sort by</label>
      <select
        id={id}
        value={value}
        onChange={(event) => {
          const [key, direction] = event.target.value.split(':');
          onChange(
            key && (direction === 'ascending' || direction === 'descending')
              ? { key: key as SortState['key'], direction }
              : null,
          );
        }}
      >
        <option value="">Route order</option>
        {SORT_KEYS.flatMap((key) => [
          <option key={`${key}:ascending`} value={`${key}:ascending`}>
            {SORT_LABELS[key]}, A to Z
          </option>,
          <option key={`${key}:descending`} value={`${key}:descending`}>
            {SORT_LABELS[key]}, Z to A
          </option>,
        ])}
      </select>
    </div>
  );
}
